import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { PuntajesTerman, SubtestTermanResultado } from './types.js';

// Mismo cálculo que el "Perfil por subprueba" de lógica-final-terman.html:
// puntaje de la subprueba expresado sobre 100.
function porcentaje(st: SubtestTermanResultado): number {
  return st.max > 0 ? Math.round((st.score * 100) / st.max) : 0;
}

// Etiquetas que emite el backend: CL_D usa "Término Medio Bajo/Alto" para el CI
// global y RANGOS usa la forma corta "T.Medio Bajo/Alto" por subprueba.
const CLASE_POR_RANGO: Record<string, string> = {
  'Deficiente':         'badge-danger',
  'Inferior':           'badge-danger',
  'Término Medio Bajo': 'badge-warning',
  'T.Medio Bajo':       'badge-warning',
  'Término Medio':      'badge-neutral',
  'Término Medio Alto': 'badge-info',
  'T.Medio Alto':       'badge-info',
  'Superior':           'badge-success',
  'Excelente':          'badge-success',
  'Sobresaliente':      'badge-success',
};

function rangoBadge(rango: string) {
  return <span className={`badge ${CLASE_POR_RANGO[rango] ?? 'badge-neutral'}`}>{rango}</span>;
}

function TermanResumen({ puntajes }: { puntajes: PuntajesTerman }) {
  const cajas = [
    { valor: puntajes.ci, etiqueta: 'Coeficiente Intelectual' },
    { valor: puntajes.total_score, etiqueta: 'Puntos totales' },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap', marginBottom: 12 }}>
      {cajas.map(({ valor, etiqueta }) => (
        <div key={etiqueta}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--heading)', lineHeight: 1.1 }}>{valor}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{etiqueta}</div>
        </div>
      ))}
      <div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Clasificación</div>
        {rangoBadge(puntajes.clasificacion)}
      </div>
    </div>
  );
}

function TermanPerfilChart({ subtests }: { subtests: SubtestTermanResultado[] }) {
  const data = subtests.map((st) => ({ subprueba: `${st.id}. ${st.nombre}`, Perfil: porcentaje(st) }));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 8, right: 24, left: 28, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="subprueba" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} />
        <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 11 }} width={32} />
        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
        <Line type="monotone" dataKey="Perfil" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 5, fill: '#fbbf24' }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function TermanTabla({ subtests }: { subtests: SubtestTermanResultado[] }) {
  return (
    <div className="table-scroll" style={{ marginTop: 16 }}>
      <table className="main-table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>#</th>
            <th>Subprueba</th>
            <th>Puntaje</th>
            <th>Aciertos</th>
            <th>Errores</th>
            <th>En blanco</th>
            <th>/ 100</th>
            <th>Rango</th>
          </tr>
        </thead>
        <tbody>
          {subtests.map((st) => (
            <tr key={st.id}>
              <td style={{ fontWeight: 700 }}>{st.id}</td>
              <td style={{ fontWeight: 600 }}>{st.nombre}</td>
              <td style={{ fontWeight: 600 }}>{st.score} / {st.max}</td>
              <td>{st.ac}</td>
              <td>{st.err}</td>
              <td>{st.blancos}</td>
              <td>{porcentaje(st)}</td>
              <td>{rangoBadge(st.rango)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TermanResultado({ puntajes }: { puntajes: PuntajesTerman }) {
  return (
    <>
      <TermanResumen puntajes={puntajes} />
      <TermanPerfilChart subtests={puntajes.subtests} />
      <TermanTabla subtests={puntajes.subtests} />
    </>
  );
}
